import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Sparkles } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MouseGlow from '../ui/MouseGlow';
import heroIllustration from '../../assets/hero-illustration-pictos.png';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../hooks/useAuth';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

const LandingPage = ({ onLogin }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { login, loading, error, setError } = useAuth();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    const validateEmail = (email) => {
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateEmail(email)) {
            setError(t.auth?.invalidEmail || 'Invalid email address');
            return;
        }

        if (!code) {
            setError(t.auth?.enterCode || 'Please enter your code');
            return;
        }

        // Use the useAuth hook for login
        const result = await login(email, code);

        if (result.success) {
            setIsRedirecting(true);
            setTimeout(() => {
                window.location.href = '/';
            }, 1500);
        }
        // Error is automatically set by the hook
    };

    // const handleGoogleLogin = async () => {
    //     setError('');
    //     setLoading(true);
    //     try {
    //         window.location.href = 'https://apicabaza.creago.be/api/rest/userAuth/google';
    //     } catch (err) {
    //         setError(err.message);
    //         setLoading(false);
    //     }
    // };

    if (isRedirecting) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="relative w-24 h-24 mx-auto mb-6">
                        <div className="absolute inset-0 rounded-full border-4 border-cabaza-purple/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-cabaza-purple animate-spin"></div>
                        <div className="absolute inset-3 rounded-full border-4 border-cabaza-pink/20"></div>
                        <div className="absolute inset-3 rounded-full border-4 border-transparent border-t-cabaza-pink animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                        <div className="absolute inset-8 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink animate-pulse"></div>
                    </div>
                    <p className="text-lg text-gray-600 font-medium">{t.auth?.loggingIn || 'Logging in...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white relative">
            <MouseGlow />
            <Header onLogin={onLogin} />

            <main>
                {/* Hero Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        {/* Left Content - Login Form */}
                        <div className="space-y-6 animate-fade-in-up">
                            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
                                <span className="block text-cabaza-purple">{t.landing?.title1 || 'Connect'}</span>
                                <span className="block">{t.landing?.title2 || 'simply'}</span>
                            </h1>

                            {/* Slogan */}
                            <p className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent">
                                {t.landing?.slogan || 'Le réseau social européen pour échanger'}
                            </p>

                            {/* Description */}
                            <p className="text-base text-gray-600">
                                {t.landing?.description || 'Cabaza réunit les européens qui veulent partager leurs compétences, services et passions. Rejoignez une communauté où chaque échange crée de nouvelles opportunités.'}
                            </p>

                            {!isRegistrationDisabled && (
                                <p className="text-lg text-gray-600">
                                    {t.landing?.subtitle || 'Manage your contacts and business cards with ease and start offering or requesting services.'}
                                </p>
                            )}

                            {/* Login Form */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-md">
                                {/* Error message */}
                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                        <p className="text-red-800 text-sm">{error}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Email field */}
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t.auth?.email || 'Email address'}
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cabaza-purple transition-colors"
                                                placeholder={t.auth?.emailPlaceholder || 'example@email.com'}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Code field */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                                                {t.auth?.accessCode || 'Access code'}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => navigate('/login')}
                                                className="text-xs text-cabaza-purple hover:text-cabaza-pink transition-colors"
                                            >
                                                {t.auth?.forgotCode || 'Forgot code?'}
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                id="code"
                                                type="password"
                                                value={code}
                                                onChange={(e) => setCode(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cabaza-purple transition-colors"
                                                placeholder="••••••"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Submit button */}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                {t.auth?.loggingIn || 'Logging in...'}
                                            </span>
                                        ) : (
                                            t.auth?.login || 'Log in'
                                        )}
                                    </button>
                                </form>

                                {/* Google login - Commenté */}
                                {/* <div className="my-4 flex items-center gap-4">
                                    <div className="flex-1 h-px bg-gray-200"></div>
                                    <span className="text-sm text-gray-400">ou</span>
                                    <div className="flex-1 h-px bg-gray-200"></div>
                                </div>

                                <button
                                    onClick={handleGoogleLogin}
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    <span className="font-medium text-gray-700">Connexion avec Google</span>
                                </button> */}

                                {/* Create account link (hidden if registration is disabled) */}
                                {!isRegistrationDisabled && (
                                    <div className="mt-6 pt-4 border-t border-gray-100">
                                        <p className="text-center text-sm text-gray-500 mb-3">
                                            {t.auth?.noAccount || "Don't have an account?"}
                                        </p>
                                        <Link
                                            to="/register"
                                            className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all"
                                        >
                                            <Sparkles className="w-5 h-5" />
                                            {t.auth?.createAccountFree || 'Create a free account'}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Content - Illustration */}
                        <div className="relative animate-fade-in hidden md:block">
                            <div className="absolute inset-0 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full filter blur-3xl opacity-30 transform scale-110"></div>
                            <img
                                src={heroIllustration}
                                alt="Professionals connecting"
                                className="relative z-10 w-full h-auto hover:scale-105 transition-transform duration-500"
                            />
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default LandingPage;
